class GenderToggle extends HTMLElement {
  connectedCallback() {
    this.cookieName = this.dataset.cookie || 'gb-audience';
    this.defaultGender = this.dataset.default || 'women';
    const forced = this.dataset.force;

    if (forced === 'women' || forced === 'men') {
      this.active = forced;
      if (this.readCookie() !== forced) this.writeCookie(forced);
    } else {
      this.active = this.readCookie() || this.defaultGender;
    }

    this.sync();
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick = (event) => {
    const option = event.target.closest('[data-gender]');
    if (!option || !this.contains(option)) return;

    const gender = option.dataset.gender;
    this.writeCookie(gender);
    this.active = gender;
    this.sync();

    const href = option.getAttribute('href');
    if (!href || href === '#' || option.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
    }
  };

  sync() {
    this.querySelectorAll('[data-gender]').forEach((option) => {
      const isActive = option.dataset.gender === this.active;
      option.classList.toggle('is-active', isActive);
      option.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  readCookie() {
    const match = document.cookie.match(new RegExp(`(?:^|; )${this.cookieName}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  }

  writeCookie(value) {
    document.cookie = `${this.cookieName}=${encodeURIComponent(value)}; path=/; max-age=31536000; SameSite=Lax`;
  }
}

class ResultsStories extends HTMLElement {
  connectedCallback() {
    this.stories = Array.from(this.querySelectorAll('[data-results-story]'));
    this.categoryButtons = Array.from(this.querySelectorAll('[data-results-category]:not([data-results-story])'));
    this.categorySelect = this.querySelector('[data-results-category-select]');

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    this.categorySelect?.addEventListener('change', this.onCategoryChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    this.categorySelect?.removeEventListener('change', this.onCategoryChange);
  }

  onClick = (event) => {
    const category = event.target.closest('[data-results-category]:not([data-results-story])');

    if (category && this.contains(category)) {
      event.preventDefault();
      this.selectCategory(category.dataset.resultsCategory);
      return;
    }

    // The slider is its own control, so a drag or click on it must not also select the story
    if (event.target.closest('comparison-slider')) return;

    const trigger = event.target.closest('[data-results-trigger]');
    if (!trigger || !this.contains(trigger)) return;

    event.preventDefault();
    this.selectStory(trigger.dataset.resultsTrigger);
  };

  onKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (event.target.closest('comparison-slider, select, a, button')) return;

    const trigger = event.target.closest('[data-results-trigger]');
    if (!trigger || event.target !== trigger) return;

    event.preventDefault();
    this.selectStory(trigger.dataset.resultsTrigger);
  };

  onCategoryChange = (event) => {
    this.selectCategory(event.target.value);
  };

  selectCategory(category) {
    this.categoryButtons.forEach((button) => {
      const isActive = button.dataset.resultsCategory === category;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    if (this.categorySelect) {
      this.categorySelect.value = category;
    }

    let firstMatch = null;

    this.stories.forEach((story) => {
      const matches = story.dataset.resultsCategory === category;
      story.hidden = !matches;

      if (matches && !firstMatch) {
        firstMatch = story;
      }
    });

    if (firstMatch) {
      this.selectStory(firstMatch.dataset.resultsStory);
    }
  }

  selectStory(id) {
    this.stories.forEach((story) => {
      const isActive = story.dataset.resultsStory === id;

      story.classList.toggle('is-active', isActive);
      story.querySelector('[data-results-details]')?.toggleAttribute('hidden', !isActive);
      story.setAttribute('aria-expanded', isActive ? 'true' : 'false');
    });
  }
}

class ComparisonSlider extends HTMLElement {
  connectedCallback() {
    this.media = this.querySelector('[data-comparison-media]');
    this.range = this.querySelector('[data-comparison-range]');

    if (!this.media || !this.range) return;

    this.restPosition = Number(this.range.value);
    this.timers = [];

    this.range.addEventListener('input', this.onInput);
    this.observeReveal();
  }

  disconnectedCallback() {
    this.range?.removeEventListener('input', this.onInput);
    this.observer?.disconnect();
    this.timers?.forEach(clearTimeout);
  }

  onInput = () => {
    if (this.isRevealing) this.stopReveal();
    this.setPosition(this.range.value);
  };

  setPosition(value) {
    this.media.style.setProperty('--compare', String(value));
  }

  observeReveal() {
    if (!window.IntersectionObserver) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.observer.disconnect();
        this.reveal();
      },
      { threshold: 0.5 }
    );

    this.observer.observe(this);
  }

  /* Rocks the handle once the slider is on screen so it reads as something to drag */
  reveal() {
    this.isRevealing = true;
    this.media.style.setProperty('--comparison-transition-duration', '0.5s');

    const steps = [
      [300, Math.max(this.restPosition - 12, 0)],
      [800, Math.min(this.restPosition + 12, 100)],
      [1300, this.restPosition],
    ];

    this.timers = steps.map(([delay, value]) => setTimeout(() => this.setPosition(value), delay));
    this.timers.push(setTimeout(() => this.stopReveal(), 1800));
  }

  stopReveal() {
    this.timers.forEach(clearTimeout);
    this.timers = [];
    this.media.style.setProperty('--comparison-transition-duration', '0s');
    this.isRevealing = false;
  }
}

if (!window.customElements.get('gender-toggle')) {
  window.customElements.define('gender-toggle', GenderToggle);
}

if (!window.customElements.get('results-stories')) {
  window.customElements.define('results-stories', ResultsStories);
}

if (!window.customElements.get('comparison-slider')) {
  window.customElements.define('comparison-slider', ComparisonSlider);
}

class ClinicalResultsRings extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !window.IntersectionObserver) {
      this.classList.add('is-in-view');
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.observer.disconnect();
        this.classList.add('is-in-view');
      },
      { threshold: 0.35 }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!window.customElements.get('clinical-results-rings')) {
  window.customElements.define('clinical-results-rings', ClinicalResultsRings);
}

class FaqsList extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick = (event) => {
    const summary = event.target.closest('.accordion__summary');
    if (!summary || !this.contains(summary)) return;

    const current = summary.closest('accordion-disclosure');
    if (!current || !this.contains(current)) return;
    if (!current.classList.contains('is-open')) return;

    this.querySelectorAll('accordion-disclosure.is-open').forEach((item) => {
      if (item === current) return;
      item.querySelector('.accordion__summary')?.click();
    });
  };
}

if (!window.customElements.get('gb-faqs-list')) {
  window.customElements.define('gb-faqs-list', FaqsList);
}

document.addEventListener('click', (event) => {
  const summary = event.target.closest?.('.accordion__summary');
  if (!summary) return;

  const productInfo = summary.closest('.product-info');
  if (!productInfo) return;

  const current = summary.closest('accordion-disclosure');
  if (!current || !productInfo.contains(current) || current.classList.contains('is-open')) return;

  productInfo.querySelectorAll('accordion-disclosure.is-open').forEach((item) => {
    item.querySelector('.accordion__summary')?.click();
  });
}, true);

class StatisticsCount extends HTMLElement {
  connectedCallback() {
    this.values = Array.from(this.querySelectorAll('[data-count-to]'));
    this.frames = [];
    this.values.forEach((el) => this.reset(el));

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !window.IntersectionObserver) {
      this.values.forEach((el) => this.commit(el, true));
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        this.observer.disconnect();
        this.play();
      },
      { threshold: 0.35 }
    );

    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.frames?.forEach((frame) => cancelAnimationFrame(frame));
  }

  parse(raw) {
    const text = String(raw).trim();
    const match = text.match(/^(\D*?)(-?\d+(?:[.,]\d+)?)(\D*)$/);

    if (!match) return null;

    const number = match[2].replace(',', '.');

    return {
      original: text,
      prefix: match[1],
      suffix: match[3],
      value: Number(number),
      decimals: (number.split('.')[1] || '').length,
    };
  }

  reset(el) {
    const parsed = this.parse(el.dataset.countTo);

    if (!parsed) return;

    el.dataset.countParsed = 'true';
    el.textContent = this.format(parsed, 0);
  }

  commit(el, done, current = 0) {
    const parsed = this.parse(el.dataset.countTo);

    if (!parsed) return;

    el.textContent = this.format(parsed, current, done);
  }

  format(parsed, current, done = false) {
    if (done) return parsed.original;

    const number = parsed.decimals > 0 ? current.toFixed(parsed.decimals) : String(Math.round(current));

    return `${parsed.prefix}${number}${parsed.suffix}`;
  }

  play() {
    this.values.forEach((el, index) => {
      this.animate(el, index * 120);
    });
  }

  animate(el, delay) {
    const parsed = this.parse(el.dataset.countTo);

    if (!parsed) return;

    const duration = 1400;
    const start = performance.now() + delay;
    const tick = (now) => {
      const elapsed = now - start;

      if (elapsed < 0) {
        this.frames.push(requestAnimationFrame(tick));
        return;
      }

      const progress = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - progress, 3);

      this.commit(el, progress >= 1, parsed.value * eased);

      if (progress < 1) {
        this.frames.push(requestAnimationFrame(tick));
      }
    };

    this.frames.push(requestAnimationFrame(tick));
  }
}

if (!window.customElements.get('gb-statistics-count')) {
  window.customElements.define('gb-statistics-count', StatisticsCount);
}

class PdpInfoScroll extends HTMLElement {
  connectedCallback() {
    this.scroller = this.closest('.gb-pdp__info-wrap')?.querySelector('.gb-pdp__info');
    if (!this.scroller) return;

    this.onScroll = () => this.update();
    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
    this.scroller.addEventListener('toggle', this.onScroll, true);

    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.scroller);
    if (this.scroller.firstElementChild) {
      this.resizeObserver.observe(this.scroller.firstElementChild);
    }

    this.update();
  }

  disconnectedCallback() {
    this.scroller?.removeEventListener('scroll', this.onScroll);
    this.scroller?.removeEventListener('toggle', this.onScroll, true);
    this.resizeObserver?.disconnect();
  }

  update() {
    if (!this.scroller) return;

    const { scrollTop, scrollHeight, clientHeight } = this.scroller;
    const overflow = scrollHeight - clientHeight;
    const progress = scrollHeight > 0 ? Math.min(1, (scrollTop + clientHeight) / scrollHeight) : 1;

    this.style.setProperty('--gb-pdp-info-scroll', String(progress));
    this.toggleAttribute('hidden', overflow <= 1);
  }
}

if (!window.customElements.get('gb-pdp-info-scroll')) {
  window.customElements.define('gb-pdp-info-scroll', PdpInfoScroll);
}

class ProofLoop {
  constructor(carousel) {
    this.carousel = carousel;
    this.media = window.matchMedia('(max-width: 999px)');
    this.count = 0;
    this.looping = false;
    this.correcting = false;
    this.onScroll = () => this.scheduleProgress();
    this.onScrollEnd = () => this.settle();
    this.onMediaChange = () => this.sync();
    this.media.addEventListener('change', this.onMediaChange);
    this.sync();
  }

  destroy() {
    this.media.removeEventListener('change', this.onMediaChange);
    this.teardown();
  }

  sync() {
    this.teardown();
    if (!this.media.matches) return;

    const originals = [...this.carousel.querySelectorAll('.gb-proof__card')];
    if (originals.length < 2) return;

    this.count = originals.length;
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();

    originals.forEach((card) => {
      before.append(this.cloneCard(card));
      after.append(this.cloneCard(card));
    });

    this.carousel.prepend(before);
    this.carousel.append(after);
    this.looping = true;
    this.carousel.addEventListener('scroll', this.onScroll, { passive: true });
    this.carousel.addEventListener('scrollend', this.onScrollEnd);
    this.carousel.select?.(this.count, { instant: true });
    this.scheduleProgress();
  }

  teardown() {
    cancelAnimationFrame(this.frame);
    this.carousel.removeEventListener('scroll', this.onScroll);
    this.carousel.removeEventListener('scrollend', this.onScrollEnd);
    this.carousel.querySelectorAll('[data-gb-proof-clone]').forEach((card) => card.remove());
    this.looping = false;
    this.correcting = false;
    this.count = 0;
  }

  cloneCard(card) {
    const copy = card.cloneNode(true);
    copy.setAttribute('data-gb-proof-clone', '');
    copy.setAttribute('aria-hidden', 'true');
    copy.removeAttribute('data-shopify-editor-block');
    return copy;
  }

  settle() {
    if (!this.looping) return;

    if (this.correcting) {
      this.correcting = false;
      this.scheduleProgress();
      return;
    }

    const index = this.carousel.selectedIndex;
    const realIndex = this.count + (index % this.count);

    if (index !== realIndex) {
      this.correcting = true;
      this.carousel.select(realIndex, { instant: true });
    }

    this.scheduleProgress();
  }

  scheduleProgress() {
    if (!this.looping) return;

    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => {
      const bar = this.carousel.closest('.gb-proof__stats-wrap')?.querySelector('.gb-proof__progress');
      if (!bar || this.count < 2) return;

      const logical = this.carousel.selectedIndex % this.count;
      bar.style.setProperty('--progress-bar-progress', String((logical + 1) / this.count));
    });
  }
}

const proofLoops = new WeakMap();

function initProofLoops(root = document) {
  root.querySelectorAll('scroll-carousel.gb-proof__list--peek').forEach((carousel) => {
    if (proofLoops.has(carousel)) return;
    proofLoops.set(carousel, new ProofLoop(carousel));
  });
}

initProofLoops();
document.addEventListener('shopify:section:load', (event) => initProofLoops(event.target));
